import * as React from 'react'
import {
  AlertTriangle, CheckCircle2, ExternalLink, HelpCircle, Loader2, Plus, Settings2,
  ShieldAlert, Trash2, XCircle, Zap,
} from 'lucide-react'

import {
  useRegistrarProviders, useSaveRegistrarProvider, useSetPremiumRules, useSetRegistrarSwitches,
  useTestProviderCheck, useTestProviderRegister,
  type PremiumRule, type ProviderAccountView, type ProviderPatch,
  type ProviderTestResult, type RegistrarProvider,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Label, Switch } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

function toMajor(minor?: number): string {
  return minor == null ? '' : (minor / 100).toFixed(2)
}

/**
 * 注册商接口配置与测试台。
 *
 * 平台级、仅管理员：一把 API Key 是部署的属性，不是某个人的偏好。两个人各自
 * 配一套凭据，账单和查注比就再也对不上了。
 */
export default function RegistrarProvidersPage() {
  const { data, isLoading, error, refetch } = useRegistrarProviders()
  const providers = data?.providers ?? []
  const [selectedProviderID, setSelectedProviderID] = React.useState('')
  const [rulesOpen, setRulesOpen] = React.useState(false)

  React.useEffect(() => {
    if (!providers.length) return
    setSelectedProviderID((current) => (
      providers.some((provider) => provider.id === current)
        ? current
        : (providers.find((provider) => provider.config.enabled) ?? providers[0]).id
    ))
  }, [data?.providers])

  const selectedProvider = providers.find((provider) => provider.id === selectedProviderID)

  return (
    <div>
      <PageHeader
        title="注册商接口"
        description="按顺序完成能力开关、服务商配置和接口验证。真实注册操作会扣款。"
        actions={data && (
          <Button variant="outline" size="sm" onClick={() => setRulesOpen(true)}>
            <Settings2 className="h-3.5 w-3.5" aria-hidden /> 全局溢价规则
            {(data.premium_rules?.length ?? 0) > 0 && <Badge variant="muted">{data.premium_rules!.length}</Badge>}
          </Button>
        )}
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : data ? (
        <div className="space-y-8">
          <SetupOverview
            availabilityEnabled={data.availability_enabled}
            registrationEnabled={data.registration_enabled}
            providers={providers}
          />

          <section id="provider-capabilities" className="scroll-mt-20 space-y-3">
            <WorkflowHeading
              step={1}
              title="设置平台能力"
              description="先决定平台能否调用查询接口，以及是否允许后台产生真实扣款。"
            />
            <SwitchPanel
              registrationEnabled={data.registration_enabled}
              availabilityEnabled={data.availability_enabled}
              registrationReady={data.registration_ready}
              order={data.enabled_order ?? []}
              providers={providers}
            />
          </section>

          <section id="provider-credentials" className="scroll-mt-20 space-y-3">
            <WorkflowHeading
              step={2}
              title="配置服务商"
              description="从列表中选择一家服务商，只编辑当前服务商的连接、账户凭据和下单顺序。"
            />
            <ProviderWorkspace
              providers={providers}
              selectedID={selectedProviderID}
              onSelect={setSelectedProviderID}
              selected={selectedProvider}
            />
          </section>

          <section id="provider-verification" className="scroll-mt-20 space-y-3">
            <WorkflowHeading
              step={3}
              title="验证接口"
              description="配置完成后先运行只读查询；需要时再切换到真实注册测试。"
            />
            <TestBench providers={providers} />
          </section>

          {rulesOpen && (
            <PremiumRulesDialog
              rules={data.premium_rules ?? []}
              open
              onOpenChange={setRulesOpen}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

function SetupOverview({
  availabilityEnabled, registrationEnabled, providers,
}: {
  availabilityEnabled: boolean
  registrationEnabled: boolean
  providers: RegistrarProvider[]
}) {
  const enabled = providers.filter((provider) => provider.config.enabled).length
  const ready = providers.filter((provider) => provider.config.enabled && provider.config.ready_to_register).length
  const checkable = providers.filter((provider) => provider.config.ready_to_check).length
  const steps = [
    {
      href: '#provider-capabilities',
      title: '平台能力',
      status: availabilityEnabled || registrationEnabled ? '已设置' : '均已关闭',
      description: `状态复核${availabilityEnabled ? '已开' : '已关'} · 自动下单${registrationEnabled ? '已开' : '已关'}`,
      ready: availabilityEnabled || registrationEnabled,
    },
    {
      href: '#provider-credentials',
      title: '服务商配置',
      status: `${enabled} 家启用`,
      description: `${ready} 家可下单 · ${checkable} 家可查询`,
      ready: enabled > 0 && (ready > 0 || checkable > 0),
    },
    {
      href: '#provider-verification',
      title: '接口验证',
      status: checkable > 0 ? '可以开始' : '等待凭据',
      description: '先只读查询，再按需进行真实注册测试',
      ready: checkable > 0,
    },
  ]

  return (
    <nav aria-label="注册商接口配置步骤" className="grid overflow-hidden rounded-lg border bg-card md:grid-cols-3">
      {steps.map((step, index) => (
        <a
          key={step.href}
          href={step.href}
          className="group flex min-w-0 items-start gap-3 border-b p-4 transition-colors hover:bg-muted/40 md:border-b-0 md:border-r md:last:border-r-0"
        >
          <span className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
            step.ready ? 'border-primary/30 bg-primary/10 text-primary' : 'text-muted-foreground',
          )}>
            {index + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{step.title}</span>
              <Badge variant={step.ready ? 'secondary' : 'muted'}>{step.status}</Badge>
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">{step.description}</span>
          </span>
        </a>
      ))}
    </nav>
  )
}

function WorkflowHeading({ step, title, description }: {
  step: number
  title: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">
        {step}
      </span>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

function ProviderWorkspace({ providers, selectedID, onSelect, selected }: {
  providers: RegistrarProvider[]
  selectedID: string
  onSelect: (id: string) => void
  selected?: RegistrarProvider
}) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card lg:grid lg:grid-cols-[250px_minmax(0,1fr)]">
      <div className="border-b bg-muted/20 lg:border-b-0 lg:border-r">
        <div className="border-b px-3 py-2.5">
          <p className="text-xs font-medium text-muted-foreground">服务商</p>
        </div>
        <div className="max-h-[720px] space-y-0.5 overflow-y-auto p-2">
          {providers.map((provider) => {
            const cfg = provider.config
            const active = provider.id === selectedID
            const status = !cfg.enabled
              ? '未启用'
              : cfg.ready_to_register
                ? '可查询、可注册'
                : cfg.ready_to_check
                  ? '仅可查询'
                  : '待补凭据'
            const statusTone = !cfg.enabled
              ? 'bg-muted-foreground/40'
              : cfg.ready_to_register
                ? 'bg-emerald-500'
                : cfg.ready_to_check
                  ? 'bg-amber-500'
                  : 'bg-destructive'
            return (
              <button
                key={provider.id}
                type="button"
                onClick={() => onSelect(provider.id)}
                className={cn(
                  'flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                  active ? 'bg-primary/10 text-primary' : 'hover:bg-accent',
                )}
              >
                <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', statusTone)} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{provider.name}</span>
                  <span className={cn('block text-[0.7rem]', active ? 'text-primary/70' : 'text-muted-foreground')}>
                    {status}
                  </span>
                </span>
                {cfg.enabled && <span className="text-[0.65rem] tabular-nums text-muted-foreground">P{cfg.priority}</span>}
              </button>
            )
          })}
        </div>
      </div>

      <div className="min-w-0">
        {selected ? (
          <ProviderCard key={selected.id} provider={selected} />
        ) : (
          <p className="p-8 text-center text-sm text-muted-foreground">请选择一家服务商</p>
        )}
      </div>
    </div>
  )
}

/** 平台级两个开关 + 下单顺序说明。 */
function SwitchPanel({
  registrationEnabled, availabilityEnabled, registrationReady, order, providers,
}: {
  registrationEnabled: boolean
  availabilityEnabled: boolean
  registrationReady: boolean
  order: string[]
  providers: RegistrarProvider[]
}) {
  const toast = useToast()
  const setSwitches = useSetRegistrarSwitches()
  const nameOf = (id: string) => providers.find((p) => p.id === id)?.name ?? id

  async function flip(patch: { registration_enabled?: boolean; availability_enabled?: boolean }) {
    try {
      await setSwitches.mutateAsync(patch)
      toast.success('已保存')
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="grid gap-4 border-b p-4 sm:grid-cols-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium">可注册状态复核</h2>
            <p className="text-xs text-muted-foreground">
              允许用注册商接口复核 RDAP 的判断。只读，不花钱。关掉则一切以 RDAP 结论为准。
            </p>
          </div>
          <Switch
            checked={availabilityEnabled}
            disabled={setSwitches.isPending}
            onCheckedChange={(v) => void flip({ availability_enabled: v })}
            aria-label="可注册状态复核"
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium">允许自动下单（会花钱）</h2>
            <p className="text-xs text-muted-foreground">
              总开关。关掉后无论勾选了多少服务商，后台任务都不会下单。
              这是唯一一个能立刻停掉所有支出的开关。
            </p>
          </div>
          <Switch
            checked={registrationEnabled}
            disabled={setSwitches.isPending}
            onCheckedChange={(v) => void flip({ registration_enabled: v })}
            aria-label="允许自动下单"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-3 text-xs">
        {order.length === 0 ? (
          <span className="text-muted-foreground">还没有勾选任何服务商。</span>
        ) : (
          <>
            <span className="text-muted-foreground">下单顺序：</span>
            {order.map((id, i) => (
              <React.Fragment key={id}>
                {i > 0 && <span className="text-muted-foreground">→</span>}
                <Badge variant="secondary">{nameOf(id)}</Badge>
              </React.Fragment>
            ))}
            <Tooltip content="按「优先级」从小到大依次尝试；某一家明确拒绝就问下一家。但如果某一家的结果「未知」（超时、响应读不出来），整条链会立刻停下——它可能已经买成了，再问下一家就是买两次。">
              <HelpCircle className="h-3.5 w-3.5 cursor-help text-muted-foreground" aria-hidden />
            </Tooltip>
          </>
        )}
        {registrationEnabled && !registrationReady && (
          <span className="ml-auto inline-flex items-center gap-1 text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            开关已开，但没有一个已勾选的服务商具备可下单的完整凭据
          </span>
        )}
      </div>
    </div>
  )
}

/** 单个服务商的凭据卡片。 */
function ProviderCard({ provider }: { provider: RegistrarProvider }) {
  const toast = useToast()
  const save = useSaveRegistrarProvider()
  const cfg = provider.config

  const [endpoint, setEndpoint] = React.useState(cfg.endpoint ?? '')
  const [priority, setPriority] = React.useState(String(cfg.priority ?? 0))

  async function persist(patch: Parameters<typeof save.mutateAsync>[0]['patch']) {
    try {
      await save.mutateAsync({ provider: provider.id, patch })
      toast.success('已保存', provider.name)
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  const configured = cfg.accounts.some((a) => Object.values(a.filled).some(Boolean))

  return (
    <div className="bg-card">
      <div className="flex items-start justify-between gap-4 border-b p-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-medium">{provider.name}</h3>
            {provider.drop_catch && (
              <Tooltip content="有专门的掉落抢注（drop-catch）接口，而不只是「注册快一点」">
                <Badge variant="success"><Zap className="h-3 w-3" aria-hidden />抢注</Badge>
              </Tooltip>
            )}
            <Badge variant={provider.can_check ? 'secondary' : 'muted'}>
              {provider.can_check ? '查询 ✓' : '无查询接口'}
            </Badge>
            <Badge variant={provider.can_register ? 'secondary' : 'muted'}>
              {provider.can_register ? '注册 ✓' : '无注册接口'}
            </Badge>
            {configured && (
              <Badge variant={cfg.ready_to_register ? 'success' : cfg.ready_to_check ? 'warning' : 'danger'}>
                {cfg.ready_to_register ? '可查询可注册' : cfg.ready_to_check ? '仅可查询' : '凭据不全'}
              </Badge>
            )}
            {provider.docs_url && (
              <a
                href={provider.docs_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
              >
                文档<ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            )}
          </div>
          {provider.notes && (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{provider.notes}</p>
          )}
          {provider.error && (
            <p className="mt-1 inline-flex items-start gap-1 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />{provider.error}
            </p>
          )}
          {cfg.accounts.length > 1 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {cfg.accounts.length} 套凭据，各自独立抢注——同一家注册商的多个账号是
              多条独立通道，限流和队列都分开算。
            </p>
          )}
        </div>
        <label className="flex shrink-0 items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs">
          <Checkbox
            checked={cfg.enabled}
            disabled={save.isPending}
            onCheckedChange={(v) => void persist({ enabled: v === true })}
            aria-label={`启用 ${provider.name}`}
          />
          启用此服务商
        </label>
      </div>

      <div className="space-y-6 p-4">
        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-medium">连接与下单顺序</h4>
            <p className="text-xs text-muted-foreground">先确认接口环境，再设置多服务商依次尝试的顺序。</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">接口地址</Label>
              <Input
                className="h-9 font-mono text-xs" value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder={provider.default_endpoint}
                onBlur={() => { if (endpoint !== (cfg.endpoint ?? '')) void persist({ endpoint }) }}
              />
              {provider.sandbox_endpoint && (
                <button
                  type="button"
                  className="text-[0.7rem] text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => { setEndpoint(provider.sandbox_endpoint!); void persist({ endpoint: provider.sandbox_endpoint! }) }}
                >
                  使用沙箱地址
                </button>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">下单优先级</Label>
              <Input
                className="h-9" type="number" value={priority}
                onChange={(e) => setPriority(e.target.value)}
                onBlur={() => void persist({ priority: Number(priority) || 0 })}
              />
              <p className="text-[0.7rem] text-muted-foreground">数字越小越先尝试</p>
            </div>
          </div>
        </section>

        <section className="space-y-3 border-t pt-5">
          <div>
            <h4 className="text-sm font-medium">账户与凭据</h4>
            <p className="text-xs text-muted-foreground">每套凭据是一条独立通道，保存后会立即重新检查可用能力。</p>
          </div>
          {cfg.accounts.map((acct) => (
            <AccountForm
              key={acct.id}
              provider={provider}
              account={acct}
              pending={save.isPending}
              onSave={(patch) => void persist({ ...patch, account: acct.id })}
              onRemove={acct.id === 'default' ? undefined : () => void persist({ remove_account: acct.id })}
            />
          ))}

          <AddAccountRow
            disabled={save.isPending}
            onAdd={(id) => void persist({ add_account: id })}
          />
        </section>
      </div>
    </div>
  )
}

/**
 * 溢价规则弹窗。
 *
 * 规则是全平台共用的——注册局溢价是注册局的属性，「.xyz 纯数字是溢价」在
 * 每一家卖 .xyz 的注册商那里都同样成立，所以入口属于页面级配置而不是某张
 * 服务商卡片。
 */
function PremiumRulesDialog({
  rules, open, onOpenChange,
}: { rules: PremiumRule[]; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast()
  const save = useSetPremiumRules()
  const [draft, setDraft] = React.useState<PremiumRule[]>(rules)
  const [pattern, setPattern] = React.useState('')
  const [premium, setPremium] = React.useState(true)
  const [note, setNote] = React.useState('')

  function add() {
    const p = pattern.trim().toLowerCase().replace(/^[.*]+/, '')
    if (!p) return
    setDraft((d) => [{ pattern: p, premium, note: note.trim() || undefined }, ...d.filter((r) => r.pattern !== p)])
    setPattern('')
    setNote('')
  }

  async function persist() {
    try {
      await save.mutateAsync(draft)
      toast.success('溢价规则已保存')
      onOpenChange(false)
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>溢价规则</DialogTitle>
          <DialogDescription>
            声明哪些域名是注册局溢价米。命中规则的域名下单时<strong>直接走溢价路径</strong>，
            不再先按普通价试一次——那一次试探必然被拒，而在掉落瞬间这一个来回就是成败之差。
            规则对所有注册商生效。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 space-y-1">
              <Label className="text-xs">后缀或域名</Label>
              <Input
                className="h-9 font-mono text-xs" value={pattern}
                placeholder="xyz　或　12347799.xyz"
                onChange={(e) => setPattern(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
              />
            </div>
            <div className="min-w-[10rem] flex-1 space-y-1">
              <Label className="text-xs">备注</Label>
              <Input className="h-9" value={note} placeholder="选填" onChange={(e) => setNote(e.target.value)} />
            </div>
            <label className="flex h-9 items-center gap-2 text-xs">
              <Checkbox checked={premium} onCheckedChange={(v) => setPremium(v === true)} />
              是溢价
            </label>
            <Button size="sm" variant="outline" onClick={add} disabled={!pattern.trim()}>
              <Plus className="h-3.5 w-3.5" aria-hidden /> 添加
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            优先级按<strong>精确程度</strong>，不是列表顺序：具体域名压过后缀，长后缀压过短后缀。
            所以「<code>xyz</code> 是溢价」加上「<code>ordinary.xyz</code> 不是溢价」可以正常共存。
          </p>

          <div className="max-h-72 overflow-auto rounded-lg border">
            {draft.length === 0 ? (
              <p className="p-6 text-center text-xs text-muted-foreground">
                还没有规则。没有规则时由系统自行探测：先按普通价下单，被注册商判定为溢价后再取报价确认。
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    {['后缀 / 域名', '判定', '备注', ''].map((h) => (
                      <th key={h} scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draft.map((r) => (
                    <tr key={r.pattern} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono text-xs">{r.pattern}</td>
                      <td className="px-3 py-2">
                        <Badge variant={r.premium ? 'warning' : 'muted'}>{r.premium ? '溢价' : '非溢价'}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.note || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost" size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={`删除规则 ${r.pattern}`}
                          onClick={() => setDraft((d) => d.filter((x) => x.pattern !== r.pattern))}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            <span>
              规则只说明域名<strong>是什么</strong>，不放宽<strong>能花多少</strong>。
              走溢价路径时仍然会先向该注册商取一次实际报价，报价超过注册单的价格上限就终止，
              不会因为标了「溢价」就变成空白支票。
            </span>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button onClick={() => void persist()} disabled={save.isPending}>保存规则</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** 查询 / 注册测试台。 */
function TestBench({ providers }: { providers: RegistrarProvider[] }) {
  const toast = useToast()
  const check = useTestProviderCheck()
  const register = useTestProviderRegister()

  const [mode, setMode] = React.useState<'check' | 'register'>('check')
  const [domain, setDomain] = React.useState('')
  const [confirmDomain, setConfirmDomain] = React.useState('')
  const [years, setYears] = React.useState('1')
  const [maxPrice, setMaxPrice] = React.useState('20.00')
  const [currency, setCurrency] = React.useState('USD')
  const [skipPrecheck, setSkipPrecheck] = React.useState(false)
  const [allowPremium, setAllowPremium] = React.useState(true)
  const [selected, setSelected] = React.useState<string[]>([])
  const [results, setResults] = React.useState<ProviderTestResult[] | null>(null)
  const [outcome, setOutcome] = React.useState<string | null>(null)

  // 默认勾选「已启用」的那几家，但可以单独改：想在启用之前先试一把凭据，是唯
  // 一合理的顺序。
  const enabledIds = React.useMemo(
    () => providers.filter((p) => p.config.enabled).map((p) => p.id),
    [providers],
  )
  const [touched, setTouched] = React.useState(false)
  React.useEffect(() => {
    if (!touched) setSelected(enabledIds)
  }, [enabledIds, touched])

  const usable = providers.filter((p) => (mode === 'check' ? p.can_check : p.can_register))
  const chosen = selected.filter((id) => usable.some((p) => p.id === id))

  function toggle(id: string) {
    setTouched(true)
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  }

  async function run() {
    setResults(null)
    setOutcome(null)
    try {
      if (mode === 'check') {
        const res = await check.mutateAsync({ providers: chosen, domain: domain.trim().toLowerCase() })
        setResults(res.results)
      } else {
        const res = await register.mutateAsync({
          providers: chosen,
          domain: domain.trim().toLowerCase(),
          confirm_domain: confirmDomain.trim().toLowerCase(),
          years: Number(years) || 1,
          max_price_minor: Math.round(Number(maxPrice) * 100),
          currency,
          skip_precheck: skipPrecheck,
          allow_premium: allowPremium,
        })
        setResults(res.results)
        setOutcome(res.outcome)
        if (res.outcome === 'registered') toast.success('注册成功', res.domain)
        else if (res.outcome === 'unknown') toast.error('结果未知', '已转入待确认，不会重试')
        else toast.error('未注册成功')
      }
    } catch (err) {
      toast.error('执行失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  const pending = check.isPending || register.isPending
  const confirmOk = confirmDomain.trim().toLowerCase() === domain.trim().toLowerCase() && domain.trim() !== ''

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-sm font-medium">接口测试台</h2>
        <div className="flex rounded-md border p-0.5 text-xs">
          {(['check', 'register'] as const).map((m) => (
            <button
              key={m} type="button"
              onClick={() => { setMode(m); setResults(null); setOutcome(null) }}
              className={cn('rounded px-3 py-1 transition-colors',
                mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              {m === 'check' ? '查询接口（只读）' : '注册接口（真实扣款）'}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <Label className="text-xs">选择服务商（可多选）</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {providers.map((p) => {
              const supported = mode === 'check' ? p.can_check : p.can_register
              const ready = mode === 'check' ? p.config.ready_to_check : p.config.ready_to_register
              return (
                <label
                  key={p.id}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs',
                    !supported && 'opacity-40',
                    selected.includes(p.id) && supported && 'border-primary/50 bg-primary/5',
                  )}
                >
                  <Checkbox
                    checked={selected.includes(p.id)}
                    disabled={!supported}
                    onCheckedChange={() => toggle(p.id)}
                  />
                  <span>{p.name}</span>
                  {supported && !ready && (
                    <Tooltip content={
                      p.config.accounts.flatMap((a) => a.missing ?? []).length
                        ? `还缺：${[...new Set(p.config.accounts.flatMap((a) => a.missing ?? []))].join('、')}`
                        : '凭据未配置'
                    }>
                      <AlertTriangle className="h-3 w-3 text-amber-500" aria-hidden />
                    </Tooltip>
                  )}
                  {!supported && <span className="text-muted-foreground">无此接口</span>}
                </label>
              )
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">域名</Label>
            <Input
              className="h-9" value={domain} placeholder="example.com"
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>
          {mode === 'register' && (
            <div className="space-y-1">
              <Label className="text-xs">年限</Label>
              <Input className="h-9" type="number" min="1" max="10" value={years}
                onChange={(e) => setYears(e.target.value)} />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">
              {mode === 'register' ? `价格上限（${currency}）` : `对照价（${currency}）`}
            </Label>
            <Input className="h-9" type="number" step="0.01" min="0" value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)} />
            {mode === 'check' && (
              <p className="text-[0.7rem] text-muted-foreground">仅用于结果对比，不影响任何下单</p>
            )}
          </div>
          {mode === 'check' && (
            <div className="space-y-1">
              <Label className="text-xs">对照币种</Label>
              <Input className="h-9" value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </div>
          )}
        </div>

        {mode === 'check' && (
          <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
            下单路径<strong>不会</strong>取价、也不会比对上限或币种——那些请求在掉落时是纯粹的浪费。
            这里的对照只是给你看的：把注册商回报的价格和上面的对照价放在一起，
            自己判断某个后缀值不值得抢、要不要给它建注册单。
          </p>
        )}

        {mode === 'register' && (
          <div className="space-y-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <p className="flex items-start gap-2 text-xs">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
              <span>
                这会真实向注册商下单并扣款，且无法撤销。下单前<strong>不会取价</strong>——
                溢价域名按注册局要价成交，价格上限管不住实际扣款，只参与建单时的额度校验。
                本次购买会生成一条正式注册单，同样计入日上限与并发上限。
              </span>
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">再输入一次域名以确认 <span className="text-destructive">*</span></Label>
                <Input
                  className="h-9" value={confirmDomain} placeholder={domain || 'example.com'}
                  onChange={(e) => setConfirmDomain(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">币种</Label>
                <Input className="h-9" value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
              </div>
            </div>
            <label className="flex items-start gap-2">
              <Checkbox checked={allowPremium} onCheckedChange={(v) => setAllowPremium(v === true)} className="mt-0.5" />
              <span className="text-xs">
                <span className="font-medium">允许注册局溢价域名（不超过上面的价格上限）</span>
                <span className="block text-muted-foreground">
                  新后缀下的短域名、纯数字域名大多是注册局溢价米，注册商不接受「默认价」下单。
                  勾选后：命中溢价规则的域名第一个请求就带 premium=1；没命中的先按普通价试，
                  被判定为溢价再补一次带 premium=1 的。<strong>全程不取价</strong>，
                  按注册局要价成交。取消勾选则溢价米一律买不到。
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox checked={skipPrecheck} onCheckedChange={(v) => setSkipPrecheck(v === true)} className="mt-0.5" />
              <span className="text-xs">
                <span className="font-medium">下单前先查一次可注册</span>
                <span className="block text-muted-foreground">
                  仅测试台有这个选项，勾上表示<strong>跳过</strong>它。自动注册的下单路径从不查——
                  注册请求本身就是查询：卖不了的名字，注册商会直接回「不可注册」。
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={() => void run()}
            variant={mode === 'register' ? 'destructive' : 'default'}
            disabled={pending || chosen.length === 0 || !domain.trim() || (mode === 'register' && !confirmOk)}
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            {mode === 'check' ? '查询是否可注册' : '立即购买'}
          </Button>
          <span className="text-xs text-muted-foreground">
            已选 {chosen.length} 家
            {selected.length > chosen.length && `（${selected.length - chosen.length} 家不支持该接口，已忽略）`}
          </span>
        </div>

        {results && (
          <ResultTable
            results={results} mode={mode} outcome={outcome}
            ceilingMinor={Math.round(Number(maxPrice) * 100) || undefined}
            ceilingCurrency={currency}
          />
        )}
      </div>
    </div>
  )
}

function ResultTable({
  results, mode, outcome, ceilingMinor, ceilingCurrency,
}: {
  results: ProviderTestResult[]
  mode: 'check' | 'register'
  outcome: string | null
  ceilingMinor?: number
  ceilingCurrency: string
}) {
  return (
    <div className="space-y-2">
      {outcome === 'unknown' && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <span>
            结果未知：请求已发出但注册商没有给出可判读的答复。注册单已转入「结果待确认」，
            不会重试，等复核确定到底成没成。在「自动注册」页可以看到它。
          </span>
        </p>
      )}
      <div className="scroll-x rounded-lg border">
        <table className="w-full min-w-[760px] text-sm">
          <caption className="sr-only">接口测试结果，共 {results.length} 条</caption>
          <thead className="border-b bg-muted/40">
            <tr>
              {['服务商', '结果', mode === 'check' ? '可注册' : '是否注册成功', '价格',
                ...(mode === 'check' ? ['对照上限'] : []), '耗时', '说明'].map((h) => (
                <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.provider} className="border-b last:border-0 align-top">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">
                  {r.skipped ? (
                    <Badge variant="muted">未提供</Badge>
                  ) : r.unknown ? (
                    <Badge variant="warning">结果未知</Badge>
                  ) : r.ok ? (
                    <Badge variant="success"><CheckCircle2 className="h-3 w-3" aria-hidden />成功</Badge>
                  ) : (
                    <Badge variant="danger"><XCircle className="h-3 w-3" aria-hidden />失败</Badge>
                  )}
                  {r.http_status ? (
                    <span className="ml-1 text-[0.7rem] text-muted-foreground">HTTP {r.http_status}</span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs">
                  {mode === 'check'
                    ? r.available == null ? '—' : r.available
                      ? <span className="font-medium text-emerald-600">可注册</span>
                      : <span className="text-muted-foreground">已被注册 / 不可注册</span>
                    : r.registered ? <span className="font-medium text-emerald-600">已注册</span> : '—'}
                  {r.premium && (
                    <Tooltip content="注册局溢价域名：这里的价格是一次性报价，不是该后缀的标准价，续费价通常同样高。下单时必须显式接受溢价才买得到。">
                      <Badge variant="warning" className="ml-1 cursor-help">溢价</Badge>
                    </Tooltip>
                  )}
                  {r.reason && <span className="ml-1 text-muted-foreground">（{r.reason}）</span>}
                </td>
                <td className="px-3 py-2 text-xs tabular-nums">
                  {r.charged_minor
                    ? `${r.currency ?? ''} ${toMajor(r.charged_minor)}`
                    : r.price_minor
                      ? `${r.currency ?? ''} ${toMajor(r.price_minor)}`
                      : '—'}
                </td>
                {mode === 'check' && (
                  <td className="px-3 py-2 text-xs">
                    <CeilingCompare
                      priceMinor={r.price_minor}
                      quoted={r.currency}
                      ceilingMinor={ceilingMinor}
                      ceilingCurrency={ceilingCurrency}
                    />
                  </td>
                )}
                <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{r.elapsed_ms} ms</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {r.error ? <span className="break-all">{r.error}</span> : '—'}
                  {r.raw && (
                    <details className="mt-1">
                      <summary className="cursor-pointer select-none text-[0.7rem]">原始响应</summary>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 text-[0.7rem]">
                        {r.raw}
                      </pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * 一套凭据的表单。
 *
 * 密钥类字段一律从空开始：服务端不回传已存的值，把它留在组件状态里只会让下一次
 * 保存重复提交，还可能在重渲染时把密钥重新画到屏幕上。
 */
function AccountForm({
  provider, account, pending, onSave, onRemove,
}: {
  provider: RegistrarProvider
  account: ProviderAccountView
  pending: boolean
  onSave: (patch: ProviderPatch) => void
  onRemove?: () => void
}) {
  const [values, setValues] = React.useState<Record<string, string>>({})
  const [label, setLabel] = React.useState(account.label ?? '')
  const isDefault = account.id === 'default'

  return (
    <div className={cn('rounded-md border p-3', account.enabled && 'border-primary/30 bg-primary/[0.02]')}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {!isDefault && (
          <Checkbox
            checked={account.enabled}
            disabled={pending}
            onCheckedChange={(v) => onSave({ account_enabled: v === true })}
            aria-label={`启用账号 ${account.id}`}
          />
        )}
        <Badge variant="secondary" className="font-mono">{account.id}</Badge>
        <Input
          className="h-7 w-40 text-xs" value={label} placeholder="备注（选填）"
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { if (label !== (account.label ?? '')) onSave({ account_label: label }) }}
        />
        <Badge variant={account.ready_to_register ? 'success' : account.ready_to_check ? 'warning' : 'danger'}>
          {account.ready_to_register ? '可查可注册' : account.ready_to_check ? '仅可查询' : '凭据不全'}
        </Badge>
        {account.missing && account.missing.length > 0 && (
          <span className="text-xs text-amber-600">还缺：{account.missing.join('、')}</span>
        )}
        {onRemove && (
          <Button
            variant="ghost" size="icon-sm" className="ml-auto text-destructive hover:text-destructive"
            disabled={pending} onClick={onRemove} aria-label={`删除账号 ${account.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {provider.fields.map((f) => {
          const stored = account.filled[f.key]
          const isSecret = f.kind === 'secret' || f.kind === 'json'
          const common = {
            value: values[f.key] ?? (isSecret ? '' : (account.values[f.key] ?? '')),
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setValues((v) => ({ ...v, [f.key]: e.target.value })),
          }
          if (f.kind === 'bool') {
            const checked = (values[f.key] ?? account.values[f.key] ?? '') === '1'
            return (
              <label key={f.key} className="flex items-start gap-2">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => setValues((s) => ({ ...s, [f.key]: v === true ? '1' : '-' }))}
                  className="mt-0.5"
                />
                <span className="text-xs">
                  <span className="font-medium">{f.label}</span>
                  {f.hint && <span className="block text-muted-foreground">{f.hint}</span>}
                </span>
              </label>
            )
          }
          return (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">
                {f.label}
                {f.required && <span className="text-destructive"> *</span>}
                {f.required_for === 'register' && (
                  <span className="ml-1 text-[0.7rem] font-normal text-muted-foreground">（注册时必填）</span>
                )}
                {isSecret && stored && (
                  <span className="ml-1 text-[0.7rem] font-normal text-emerald-600">已保存，留空则不改动</span>
                )}
              </Label>
              {f.kind === 'json' ? (
                <Textarea rows={3} className="font-mono text-xs"
                  placeholder={stored ? '已保存；填 - 可清空' : f.hint} {...common} />
              ) : (
                <Input className="h-9" type={f.kind === 'secret' ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={isSecret && stored ? '已保存；填 - 可清空' : f.hint} {...common} />
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-2 flex justify-end">
        <Button
          size="sm" variant="outline" disabled={pending}
          onClick={() => { onSave({ values }); setValues({}) }}
        >
          保存这套凭据
        </Button>
      </div>
    </div>
  )
}

/** 新增一套凭据。 */
function AddAccountRow({ disabled, onAdd }: { disabled: boolean; onAdd: (id: string) => void }) {
  const [id, setId] = React.useState('')
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 space-y-1">
        <Label className="text-xs">新增账号</Label>
        <Input
          className="h-9 font-mono text-xs" value={id}
          placeholder="账号标识，如 b、c（仅小写字母数字与 - _）"
          onChange={(e) => setId(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && id.trim()) { e.preventDefault(); onAdd(id); setId('') } }}
        />
      </div>
      <Button
        size="sm" variant="outline" disabled={disabled || !id.trim()}
        onClick={() => { onAdd(id); setId('') }}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> 添加
      </Button>
    </div>
  )
}

/**
 * 把注册商的报价和你填的对照价摆在一起。
 *
 * 纯展示。下单路径不做这个比较——那需要先取一次价，而在掉落瞬间那个往返比它
 * 省下的钱更贵。这里做，是因为你在挑域名的时候本来就该看清楚要花多少。
 */
function CeilingCompare({
  priceMinor, quoted, ceilingMinor, ceilingCurrency,
}: {
  priceMinor?: number
  quoted?: string
  ceilingMinor?: number
  ceilingCurrency: string
}) {
  if (!ceilingMinor) return <span className="text-muted-foreground">未填对照价</span>
  if (!priceMinor) return <span className="text-muted-foreground">未报价</span>

  // 币种不同就没法比。88 美元和 200 人民币哪个大，要汇率才知道，而这里没有——
  // 与其按一个猜来的汇率给出错误结论，不如直说比不了。
  if (quoted && quoted.toUpperCase() !== ceilingCurrency.toUpperCase()) {
    return (
      <Tooltip content="报价币种与对照价币种不同，没有汇率就无法比较">
        <Badge variant="muted" className="cursor-help">
          币种不同（{quoted} / {ceilingCurrency}）
        </Badge>
      </Tooltip>
    )
  }

  const diff = priceMinor - ceilingMinor
  if (diff > 0) {
    return (
      <Badge variant="danger">超出 {ceilingCurrency} {(diff / 100).toFixed(2)}</Badge>
    )
  }
  return (
    <Badge variant="success">未超，剩 {ceilingCurrency} {(-diff / 100).toFixed(2)}</Badge>
  )
}
